import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {formatDateTime} from '../shared/DocumentApi'
import {CvActions} from '../shared/CvActions'
import {TagChips} from '../tag/TagChips'
import {getApplication} from './Api'
import {
  formatAnswer,
  fullName,
  IApplicationModel,
  TRACKING_STATUS_BADGE,
  trackingStatusLabel,
} from './ApplicationModel'

type Props = {
  applicationId: string | null
  onClose: () => void
}

const InfoRow: FC<{label: string; value?: React.ReactNode}> = ({label, value}) => (
  <div className='d-flex py-2 border-bottom border-gray-200'>
    <span className='text-muted fw-semibold' style={{width: 170, flexShrink: 0}}>
      {label}
    </span>
    <span className='text-gray-800'>{value || '-'}</span>
  </div>
)

/** Başvurunun tüm bilgileri: kişisel bilgiler, CV, onaylar ve soru cevapları */
export const ApplicationDetailModal: FC<Props> = ({applicationId, onClose}) => {
  const [application, setApplication] = useState<IApplicationModel | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!applicationId) return
    setApplication(null)
    setLoading(true)
    getApplication(applicationId)
      .then(({data}) => setApplication(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Başvuru yüklenemedi')))
      .finally(() => setLoading(false))
  }, [applicationId])

  return (
    <Modal show={!!applicationId} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>Başvuru Detayı</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>

      <div className='modal-body'>
        {loading || !application ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-primary' />
          </div>
        ) : (
          <>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-6'>
              <div>
                <div className='fs-3 fw-bolder text-gray-900'>{fullName(application)}</div>
                <div className='text-muted'>{application.jobTitle}</div>
                <TagChips tags={application.tags} />
              </div>
              <CvActions documentId={application.documentId} fileName={`${fullName(application)} CV`} />
            </div>

            <InfoRow label='E-posta' value={application.email} />
            <InfoRow label='Telefon' value={application.phone} />
            <InfoRow label='Yaşadığı yer' value={application.candidateLocation} />
            <InfoRow label='Başvuru tarihi' value={formatDateTime(application.createdDate)} />
            <InfoRow label='İşe alım süreci' value={application.hrProcessName} />
            <InfoRow
              label='Süreç durumu'
              value={
                application.trackingStatus ? (
                  <span>
                    <span className={`badge ${TRACKING_STATUS_BADGE[application.trackingStatus]} me-2`}>
                      {trackingStatusLabel(application.trackingStatus)}
                    </span>
                    {application.currentStepName}
                  </span>
                ) : (
                  'Başlatılmadı'
                )
              }
            />
            <InfoRow
              label='Onaylar'
              value={
                <div className='d-flex flex-column fs-7'>
                  <span>Kullanım koşulları: {application.consentTerms ? 'Onaylandı' : 'Onaylanmadı'}</span>
                  <span>KVKK aydınlatma metni: {application.consentPrivacy ? 'Onaylandı' : 'Onaylanmadı'}</span>
                  <span>
                    İleride iletişim: {application.consentFutureContact ? 'İzin verdi' : 'İzin vermedi'}
                  </span>
                </div>
              }
            />

            <div className='fs-5 fw-bolder text-gray-800 mt-8 mb-3'>Soru Cevapları</div>
            {application.questions.length === 0 ? (
              <div className='text-muted'>Bu ilanda soru yok ya da cevaplanmamış.</div>
            ) : (
              application.questions.map((answer) => (
                <InfoRow key={answer.questionId} label={answer.questionLabel} value={formatAnswer(answer.value)} />
              ))
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
